import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ALL_LANGS } from '../../../constants';
import { ButtonLinkType } from '../../../graphql-fragments/ButtonLink';
import { SanityImageType } from '../../../graphql-fragments/SanityImage';
import { uniqBy } from '../../../utils/nodash';
import { getReferenceUrl } from '../../../utils/projectUtils';
import { localize } from '../../../utils/sanity';
import { slugify } from '../../../utils/utils';
import { FormField, FormFieldWithId } from '../Form';
import { CommonSettings } from '../SanityLayout';
import SmartLink from '../SmartLink';
import CTAModule from './CTAModule';

export type FormModuleProps = {
  className?: string;
};

interface LocalizedQueryData {
  sanityWebsite: CommonSettings & {
    formSection: {
      image: SanityImageType;
      title: string;
      text: string;
      contactFormTermsLegalDocumentation: {
        slug: {
          current: string;
        };
      };
      buttonsTitle?: string;
      button?: ButtonLinkType;
      addWhatsappButton?: boolean;
    };
  };
}

function FormModule(props: FormModuleProps): React.ReactElement {
  const staticData = useStaticQuery<LocalizedQueryData>(graphql`
    {
      sanityWebsite {
        contacts {
          calendlyLink
        }
        formSection {
          image {
            ...SanityImage
          }
          title {
            ...LocaleStringFragment
          }
          text {
            ...LocaleTextFragment
          }
          contactFormTermsLegalDocumentation {
            slug {
              current
            }
          }
          buttonsTitle {
            ...LocaleStringFragment
          }
          button {
            ...ButtonLink
          }
          addWhatsappButton
        }
      }
    }
  `);

  const { i18n, t } = useTranslation();

  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: LocalizedQueryData = localize(staticData, langs);

  const { sanityWebsite } = localizedData;
  const { className } = props;

  const rowFormFields: Array<FormField> = [
    {
      crmFieldId: 'name',
      fieldType: 'textSingleLine',
      isFieldRequired: true,
      title: t('form.fields.name', 'Nome'),
    },
    {
      crmFieldId: 'phoneNumber',
      fieldType: 'textSingleLine',
      inputType: 'tel',
      title: t('form.fields.phoneNumber', 'Telefone'),
    },
  ];

  const formFields: Array<FormField> = [
    {
      crmFieldId: 'email',
      fieldType: 'textSingleLine',
      isFieldRequired: true,
      title: t('form.fields.email', 'Email'),
      withEmailValidation: true,
    },
    {
      crmFieldId: 'message',
      fieldType: 'textMultiline',
      isFieldRequired: true,
      title: t('form.fields.message', 'Mensagem'),
    },
    {
      crmFieldId: 'acceptTheTerms',
      fieldType: 'singleCheckbox',
      isFieldRequired: true,
      checkBoxLabel: (
        <Trans
          i18nKey="form_section.accept_terms_checkbox_with_link"
          defaults="Li e aceito os <0>termos e condições.</0>"
          components={
            sanityWebsite.formSection.contactFormTermsLegalDocumentation.slug.current
              ? [
                  <SmartLink
                    openInNewTab
                    key="form_section.accept_terms_checkbox_with_link"
                    to={getReferenceUrl(
                      'SanityLegalDocumentation',
                      sanityWebsite.formSection.contactFormTermsLegalDocumentation.slug.current,
                    )}
                  >
                    {sanityWebsite.formSection.contactFormTermsLegalDocumentation.slug.current}
                  </SmartLink>,
                ]
              : [<span key="_">_</span>]
          }
        />
      ),
    },
    {
      crmFieldId: 'subscribeToNewsletter',
      fieldType: 'singleCheckbox',
      checkBoxLabel: <span>{t('form.fields.newsletter', 'Subscrever à nossa newsletter')}</span>,
    },
  ];

  const rowFormFieldsWithIds: Array<FormFieldWithId> = rowFormFields.map(formField => ({
    id: slugify(formField.crmFieldId!),
    ...formField,
  }));

  const formFieldsWithIds: Array<FormFieldWithId> = formFields.map(formField => ({
    id: slugify(formField.crmFieldId!),
    ...formField,
  }));

  const combinedFormFieldsWithIds = [...formFieldsWithIds, ...rowFormFieldsWithIds];

  if (combinedFormFieldsWithIds!.length !== uniqBy(combinedFormFieldsWithIds!, 'id').length) {
    throw new Error(
      'Got duplicate ids in formFieldsWithIds: ' +
        JSON.stringify(combinedFormFieldsWithIds!.map(formField => formField.id)),
    );
  }

  return (
    <CTAModule
      className={className}
      moduleId="contact"
      title={sanityWebsite.formSection.title}
      text={sanityWebsite.formSection.text}
      backgroundImage={sanityWebsite.formSection.image}
      calendlyLink={sanityWebsite.contacts.calendlyLink}
      buttonsTitle={sanityWebsite.formSection.buttonsTitle}
      addWhatsappButton={sanityWebsite.formSection.addWhatsappButton}
      contactForm={{
        rowFormFieldsWithIds: rowFormFieldsWithIds,
        formFieldsWithIds: formFieldsWithIds,
      }}
      button={sanityWebsite.formSection.button}
    />
  );
}

export default FormModule;
